import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatAuthError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, GraduationCap, Building2 } from "lucide-react";

export default function Signup() {
  // Extract supabase here
  const { signup, supabase } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "candidate" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await signup(form);
      toast.success("Account created!");
      navigate("/onboarding");
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setLoading(false); }
  };

  // Update to use Supabase OAuth
  const googleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) {
      console.error("Google signup error:", error);
      toast.error(error.message || "Failed to sign up with Google");
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-5 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Create your account</h1>
          <p className="text-zinc-400 mt-2 text-sm">Start building your personalized roadmap.</p>

          <div className="grid grid-cols-2 gap-3 mt-6">
            {[{ v: "candidate", label: "Candidate", icon: GraduationCap }, { v: "recruiter", label: "Recruiter", icon: Building2 }].map((r) => (
              <button key={r.v} type="button" data-testid={`role-${r.v}`} onClick={() => set("role", r.v)}
                className={`flex flex-col items-start gap-2 p-4 rounded-xl border transition-all ${form.role === r.v ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/25"}`}>
                <r.icon className={`w-5 h-5 ${form.role === r.v ? "text-primary" : "text-zinc-400"}`} />
                <span className="text-sm font-medium">{r.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Full name</label>
              <input data-testid="signup-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Email</label>
              <input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Password</label>
              <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)}
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all" placeholder="At least 6 characters" />
            </div>
            {error && <p data-testid="signup-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="signup-submit" disabled={loading} type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-zinc-500">OR</span><div className="flex-1 h-px bg-white/10" /></div>
          
          <button data-testid="google-signup-btn" onClick={googleLogin}
            className="w-full border border-white/15 hover:border-white/30 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>

          <p className="text-sm text-zinc-400 mt-8 text-center">
            Already have an account? <Link to="/login" data-testid="go-login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
